// Write-shaping for the Existing Attachment Field Scan tool.
//
// Pure module (imports only other pure modules) so scripts/test-scan-write.mjs
// can exercise it with plain node — equipmentOcr.js transitively imports the
// Supabase client and can't be node-imported.
//
// The verified FMX API contract this encodes (Fmx.ApiCore):
//   · PUT /v1/equipment/{id} is a partial update. `customFields` MERGES
//     PER-FIELD — entries not listed are untouched. So we send ONLY the
//     accepted rows, never an echo of the record's existing values.
//   · Echoing read-shaped entries is actively harmful: attachments-type
//     fields are read with `values: []` but reject `values` on write (400
//     fails the whole PUT); user-type fields are read as NAME strings but
//     written as integer user IDs (400); and `isHidden` is writable — echoing
//     `isHidden: true` clears the hidden field's value.
//   · Write shapes per custom field type: multi-select dropdowns take
//     `values` (array of option labels; `value` is rejected); checkboxes take
//     JSON true/false only (string "Yes" is rejected); numbers/currency
//     accept numeric strings; dates take ISO strings; single-select
//     dropdowns take the option label (case-insensitive; unknown labels are
//     rejected unless the field allows "other").
//   · A null/empty value legitimately CLEARS a field.
//   · One PUT per record is intentional: it's atomic, and the write log /
//     revert model record one row per apply. validateScanValue runs before
//     apply so a rejectable value blocks the Apply button instead of failing
//     the whole batch server-side.

// .js extensions so plain node (scripts/test-scan-write.mjs) can import this
// module — same convention as src/qbr/*.
import { parseBooleanCell, splitMultiValue, coerceCustomFieldValue } from './fmxFieldTypes.js';
import { ASSET_CONDITION_MAP, ASSET_CATEGORY_MAP } from './fmxFieldMetadata.js';

// Custom field types the scan can never write:
//   ReadOnly    — the API rejects any submitted value.
//   Attachments — writable only via attachmentIDs; nothing OCR can produce.
//   User        — requires integer user IDs; OCR extracts names.
export const UNSCANNABLE_CUSTOM_TYPES = new Set(['ReadOnly', 'Attachments', 'User']);

// Whether a field def ({ kind, key, fieldType }) can be offered as a scan
// target. System keys ending in ID/IDs are integer lookups (buildingID,
// equipmentTypeID, assignedUserIDs, …) that raw OCR text can't satisfy;
// assetCondition doesn't match the suffix and gets enum mapping below.
export function isScannableEquipmentField(def) {
  if (!def) return false;
  if (def.kind === 'custom') return !UNSCANNABLE_CUSTOM_TYPES.has(def.fieldType);
  return !/(id|ids)$/i.test(String(def.key || ''));
}

const ASSET_CONDITION_HINT =
  'Unrecognized condition — use Excellent, Good, Fair, Poor, Critical, or Not specified';

function assetConditionEnum(value) {
  const normalized = String(value).toLowerCase().trim();
  if (ASSET_CONDITION_MAP[normalized] !== undefined) return ASSET_CONDITION_MAP[normalized];
  const n = parseInt(value, 10);
  if (!isNaN(n) && n >= 1 && n <= 6 && String(n) === normalized) return n;
  return undefined;
}

const ASSET_CATEGORY_HINT =
  'Unrecognized asset category — use one of the FMX capital-planning categories (e.g. Chiller, Steam Boiler, Packaged Rooftop Unit)';

// AssetCategory writes as an integer enum (0-11). Accept a known label
// (case-insensitive) or a bare in-range integer.
function assetCategoryEnum(value) {
  const normalized = String(value).toLowerCase().trim();
  if (ASSET_CATEGORY_MAP[normalized] !== undefined) return ASSET_CATEGORY_MAP[normalized];
  const n = parseInt(value, 10);
  if (!isNaN(n) && n >= 0 && n <= 11 && String(n) === normalized) return n;
  return undefined;
}

// Normalize a post-options option entry (string or { label | value | name })
// to its label string.
function optionLabel(opt) {
  if (opt == null) return '';
  if (typeof opt === 'object') return String(opt.label ?? opt.value ?? opt.name ?? '');
  return String(opt);
}

function matchesOption(options, value) {
  const needle = String(value).trim().toLowerCase();
  return options.some(o => optionLabel(o).trim().toLowerCase() === needle);
}

// Pre-apply validation shared by the ScanDesk badge and the payload builder,
// so the UI and the write logic can never disagree. Returns null when the
// value is writable, or a short human message describing why FMX would
// reject it. A blank value is always valid (it clears the field).
export function validateScanValue(def, value) {
  if (value === null || value === undefined || value === '') return null;
  if (!def) return null; // unknown field (legacy revert rows) — passthrough

  if (def.kind === 'system') {
    if (String(def.key) === 'assetCondition' && assetConditionEnum(value) === undefined) {
      return ASSET_CONDITION_HINT;
    }
    if (String(def.key) === 'assetCategory' && assetCategoryEnum(value) === undefined) {
      return ASSET_CATEGORY_HINT;
    }
    return null;
  }

  const raw = def.raw || {};
  const options = Array.isArray(raw.options) ? raw.options : [];
  switch (def.fieldType) {
    case 'Checkbox':
      return parseBooleanCell(value) === undefined ? 'Not a yes/no value' : null;
    case 'Number':
    case 'Currency': {
      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      return cleaned === '' || isNaN(parseFloat(cleaned)) ? 'Not a number' : null;
    }
    case 'DropDownList': {
      if (options.length === 0 || raw.allowOtherOption) return null;
      const parts = raw.allowMultipleSelections ? splitMultiValue(value) : [String(value)];
      const bad = parts.find(p => !matchesOption(options, p));
      return bad !== undefined
        ? `"${bad}" is not one of this field's options — FMX will reject the write`
        : null;
    }
    default:
      return null;
  }
}

// Build the PUT /v1/equipment/{id} body from accepted rows.
//
// rows: [{ kind: 'system'|'custom', key, label, value, def }] where def is
// the fieldDef (with raw catalog metadata) or null for legacy revert rows.
// Returns { payload, skipped } — skipped rows carry a human reason and are
// never sent, so a failed coercion can't silently clear an existing value
// (input null/'' IS an intentional clear).
export function buildEquipmentPutPayload(rows) {
  const payload = {};
  const customEntries = [];
  const skipped = [];

  for (const row of rows || []) {
    if (!row || row.accepted === false) continue;
    const { kind, key, label, value, def } = row;
    const isClear = value === null || value === undefined || value === '';

    if (kind === 'system') {
      if (String(key) === 'assetCondition') {
        if (isClear) {
          // NotSpecified=1 clears the condition; a bare null on the int enum
          // would fail the request.
          payload[key] = 1;
        } else {
          const enumVal = assetConditionEnum(value);
          if (enumVal === undefined) {
            skipped.push({ key, label, reason: ASSET_CONDITION_HINT });
          } else {
            payload[key] = enumVal;
          }
        }
      } else if (String(key) === 'assetCategory') {
        if (isClear) {
          // AssetCategory has no NotSpecified sentinel — null clears it.
          payload[key] = null;
        } else {
          const enumVal = assetCategoryEnum(value);
          if (enumVal === undefined) {
            skipped.push({ key, label, reason: ASSET_CATEGORY_HINT });
          } else {
            payload[key] = enumVal;
          }
        }
      } else {
        // Strings are fine for other system props — number/date properties
        // accept numeric/ISO strings server-side; null clears.
        payload[key] = isClear ? null : value;
      }
      continue;
    }

    // Custom fields — match by customFieldID; we emit at most one entry per
    // field so the API's "duplicate IDs, last wins" rule never applies.
    const raw = def?.raw || {};
    if (raw.allowMultipleSelections) {
      // Multi-select fields reject a `value` key even for clears.
      customEntries.push({ customFieldID: key, values: isClear ? [] : splitMultiValue(value) });
      continue;
    }
    if (isClear) {
      customEntries.push({ customFieldID: key, value: null });
      continue;
    }
    if (def?.fieldType === 'Checkbox') {
      const b = parseBooleanCell(value);
      if (b === undefined) skipped.push({ key, label, reason: 'Not a yes/no value' });
      else customEntries.push({ customFieldID: key, value: b });
      continue;
    }
    if (!def) {
      // Legacy revert rows without catalog metadata: raw string passthrough
      // (the pre-fix behavior for the types we can't infer).
      customEntries.push({ customFieldID: key, value: String(value) });
      continue;
    }
    const coerced = coerceCustomFieldValue(value, def.fieldType);
    if (coerced === null) skipped.push({ key, label, reason: 'No usable value after coercion' });
    else customEntries.push({ customFieldID: key, value: coerced });
  }

  if (customEntries.length > 0) payload.customFields = customEntries;
  return { payload, skipped };
}
